// ChoralEditorialList.jsx
import React, { useEffect, useState } from "react";

const STATUS_FILTERS = [
  { value: "", label: "All states" },
  { value: "draft", label: "Draft" },
  { value: "ai_reviewed", label: "AI Reviewed" },
  { value: "editor_reviewed", label: "Editor Reviewed (Needs Verification)" },
  { value: "verified", label: "Verified" },
  { value: "archived", label: "Archived" },
];

export default function ChoralEditorialList({
  selectedId,
  onSelect,
  apiBase = "/api/admin/choral",
  totalHint,
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("editor_reviewed");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const params = new URLSearchParams();
      if (query) params.set("query", query);
      if (status) params.set("verification_status", status);
      params.set("limit", "60");
      try {
        const res = await fetch(`${apiBase}?${params.toString()}`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setItems(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (!cancelled) { setItems([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    const t = setTimeout(load, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, status, apiBase]);

  return (
    <div className="ce-list" data-testid="ce-list">
      <div className="ce-list__controls">
        <input
          type="search"
          placeholder="Search title, composer…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="ce-list-search"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="ce-list-status-filter"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value || "all"} value={f.value}>{f.label}</option>
          ))}
        </select>
        <p style={{
          margin: 0,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: "0.7rem",
          color: "var(--li-text-secondary, #5C5450)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }} data-testid="ce-list-total">
          {loading ? "Loading…" : `${total} record${total === 1 ? "" : "s"}`}
        </p>
      </div>
      <div className="ce-list__scroll">
        {items.map((it) => (
          <div
            key={it.id}
            className={
              "ce-list__row" + (selectedId === it.id ? " ce-list__row--selected" : "")
            }
            onClick={() => onSelect?.(it.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect?.(it.id); }}
            tabIndex={0}
            role="button"
            aria-pressed={selectedId === it.id}
            data-testid={`ce-list-row-${it.id}`}
          >
            <h3 className="ce-list__row-title">{it.title}</h3>
            <p className="ce-list__row-composer">{it.composer || "Anonymous"}</p>
            <span className={`ce-status-chip ce-status-chip--${it.verification_status}`}>
              {it.verification_status.replace(/_/g, " ")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
